<script setup lang="ts">
import { Head } from "@inertiajs/vue3"
import { SbList, SbListItem, SbListLabel, SbListSection, SbSectionedList } from "@unio/saltbox-ui"

import NavListItem from "@/components/NavListItem.vue"
import { useAuthUser } from "@/composables/useAuthUser"
import { useNavbar } from "@/composables/useNavbar"
import { ICON } from "@/lib/icons"
import {
  accountMembersPath,
  accountSettingsPath,
  batchesPath,
  inventoryIndexPath,
  peoplePath,
  loansPath,
  outingsPath,
  tagsPath,
  itemTypesPath,
  capturePath,
  printPath,
  inventoryExportsPath,
  profilePath,
  helpPath,
  superAdminAccountsPath,
} from "@/routes"

const { title } = useNavbar({ title: "More" })

interface Props {
  active_loan_count: number
  version: string
}

const props = defineProps<Props>()

const { authUser, userName, avatarSrc, isAccountOwner, isSuperAdmin } = useAuthUser()
</script>

<template>
  <Head :title="title" />

  <!-- Profile header -->
  <SbList>
    <SbListItem :to="profilePath()">
      <SbListSection avatar>
        <UAvatar
          :src="avatarSrc"
          :text="authUser?.initials"
          :alt="authUser?.first_name"
          size="md"
        />
      </SbListSection>
      <SbListSection>
        <SbListLabel>{{ userName }}</SbListLabel>
        <SbListLabel caption>{{ authUser?.email_address }}</SbListLabel>
      </SbListSection>
      <SbListSection side>
        <UIcon :name="ICON.chevronRight" class="size-4" />
      </SbListSection>
    </SbListItem>
  </SbList>

  <!-- Inventory -->
  <SbSectionedList label="Inventory" bordered separator>
    <NavListItem
      :to="inventoryIndexPath()"
      :icon="ICON.inventoryProgress"
      label="Inventory Progress"
      caption="Track what still needs inventorying"
    />
    <NavListItem
      :to="batchesPath()"
      :icon="ICON.batches"
      label="Batches"
      caption="Review and process bulk-capture photos"
    />
  </SbSectionedList>

  <!-- Organize -->
  <SbSectionedList label="Organize" bordered separator>
    <NavListItem
      :to="tagsPath()"
      :icon="ICON.tag"
      label="Tags"
      caption="Organize items with freeform labels"
    />
    <NavListItem
      :to="itemTypesPath()"
      :icon="ICON.itemType"
      label="Types"
      caption="Manage categories and auto-detection patterns"
    />
    <NavListItem
      :to="peoplePath()"
      :icon="ICON.people"
      label="People"
      caption="Track item ownership"
    />
    <NavListItem
      :to="loansPath()"
      :icon="ICON.loan"
      label="Loans"
      caption="Track items lent and borrowed"
    >
      <template v-if="props.active_loan_count > 0" #trailing>
        <UBadge color="neutral" variant="subtle" size="sm">
          {{ props.active_loan_count }}
        </UBadge>
      </template>
    </NavListItem>
    <NavListItem
      :to="outingsPath()"
      :icon="ICON.outing"
      label="Outings"
      caption="Pack items and check them back in"
    />
  </SbSectionedList>

  <!-- Tools -->
  <SbSectionedList label="Tools" bordered separator>
    <NavListItem
      :to="capturePath()"
      :icon="ICON.capture"
      label="Capture"
      caption="Photograph and catalog items on the go"
    />
    <NavListItem
      :to="printPath()"
      :icon="ICON.print"
      label="Print"
      caption="Labels, QR codes, and asset tag stickers"
    />
    <NavListItem
      :to="inventoryExportsPath()"
      :icon="ICON.importExport"
      label="Import / Export"
      caption="Export and import inventory data"
    />
  </SbSectionedList>

  <!-- Account -->
  <SbSectionedList label="Account" bordered separator>
    <NavListItem
      :to="accountMembersPath()"
      :icon="ICON.members"
      label="Members"
      caption="Manage who has access to your inventory"
    />
    <NavListItem
      :to="accountSettingsPath()"
      :icon="ICON.settings"
      label="Settings"
      caption="Asset code prefix and account options"
    />
    <NavListItem
      v-if="isAccountOwner"
      to="/account/access_tokens"
      :icon="ICON.apiToken"
      label="API Tokens"
      caption="Connect agents and command-line tools"
    />
  </SbSectionedList>

  <!-- Help -->
  <SbSectionedList label="Support" bordered separator>
    <NavListItem
      :to="helpPath()"
      :icon="ICON.help"
      label="Help"
      caption="Getting started and feature guide"
    />
  </SbSectionedList>

  <!-- Super Admin -->
  <SbSectionedList v-if="isSuperAdmin" label="Admin" bordered separator>
    <NavListItem
      :to="superAdminAccountsPath()"
      :icon="ICON.shield"
      label="Super Admin"
      caption="Accounts, users, settings, and backups"
    />
  </SbSectionedList>

  <!-- Version -->
  <p class="text-muted text-center text-xs">Ninjanizr v{{ props.version }}</p>
</template>
